import React from 'react';
import { User } from 'lucide-react';
import { resolveLearnerPortrait } from '../../utils/learnerAvatar';
import { getAvatarPortraitImgClass, getStudentAvatarByUrl } from '../../data/studentProfileAvatars';
import { AvatarPlanetGlow } from './AvatarPlanetGlow';

const SIZE_CLASSES = {
  xs: 'h-8 w-8',
  sm: 'h-9 w-9',
  md: 'h-[3.25rem] w-[3.25rem] sm:h-14 sm:w-14',
  lg: 'h-16 w-16',
  nav: 'h-full w-full min-h-0 min-w-0',
};

const WRAPPER_SIZE_CLASSES = {
  xs: 'h-8 w-8',
  sm: 'h-9 w-9',
  md: 'h-[3.25rem] w-[3.25rem] sm:h-14 sm:w-14',
  lg: 'h-16 w-16',
  nav: 'h-full w-full min-h-0 min-w-0',
};

/**
 * Circular learner portrait - custom avatar or level shield fallback.
 * @param {Object} props
 * @param {import('../../utils/studentProfileStorage').StudentProfileState['profile']} [props.profile]
 * @param {{ completed?: number; total?: number; pct?: number } | null} [props.progressSummary]
 * @param {boolean} [props.showEnglish]
 * @param {'xs' | 'sm' | 'md' | 'lg' | 'nav'} [props.size]
 * @param {boolean} [props.isDark]
 * @param {boolean} [props.isCommitter]
 * @param {string} [props.className]
 * @param {string} [props.ringClassName]
 * @param {'shield' | 'user'} [props.fallback]
 * @param {string} [props.alt]
 */
export function LearnerAvatar({
  profile,
  progressSummary = null,
  showEnglish = true,
  size = 'md',
  isDark = false,
  isCommitter = false,
  className = '',
  ringClassName = '',
  fallback = 'shield',
  alt = undefined,
}) {
  const portrait = resolveLearnerPortrait(profile, progressSummary, showEnglish);
  const sizeClass = SIZE_CLASSES[size] ?? SIZE_CLASSES.md;
  const wrapperSizeClass = WRAPPER_SIZE_CLASSES[size] ?? WRAPPER_SIZE_CLASSES.md;
  const resolvedAlt = alt ?? portrait.alt;
  const isCustomAvatar = portrait.type === 'avatar';
  const avatarMeta = isCustomAvatar ? getStudentAvatarByUrl(portrait.src) : null;

  const ring =
    ringClassName ||
    (portrait.type === 'avatar'
      ? 'p-0 overflow-visible'
      : isCommitter
        ? isDark
          ? 'bg-gradient-to-br from-amber-300/90 to-violet-400/60 shadow-[0_0_16px_rgba(251,191,36,0.3)] p-0.5'
          : 'bg-gradient-to-br from-amber-300 to-violet-300 shadow-md p-0.5'
        : isDark
          ? 'bg-white/10 ring-1 ring-white/15 p-0.5'
          : 'bg-white ring-1 ring-violet-200 shadow-sm p-0.5');

  if (portrait.type === 'shield' && fallback === 'user') {
    return (
      <span
        className={`inline-flex shrink-0 items-center justify-center overflow-hidden rounded-full border-2 ${sizeClass} ${
          isDark ? 'border-white/30 bg-white/10' : 'border-cyan-200/80 bg-white'
        } ${className}`}
        aria-hidden
      >
        <User className="h-[55%] w-[55%]" style={{ color: '#12c2e9' }} strokeWidth={2} />
      </span>
    );
  }

  return (
    <span
      className={`inline-flex aspect-square shrink-0 rounded-full ${wrapperSizeClass} ${ring} ${
        isCustomAvatar ? 'items-center justify-center overflow-visible' : 'overflow-hidden'
      } ${className}`}
    >
      {isCustomAvatar ? (
        <AvatarPlanetGlow avatarId={avatarMeta?.id} className="h-full w-full">
          <img
            src={portrait.src}
            alt={resolvedAlt}
            className={getAvatarPortraitImgClass(size)}
            draggable={false}
            loading="lazy"
            decoding="async"
          />
        </AvatarPlanetGlow>
      ) : (
        <img
          src={portrait.src}
          alt={resolvedAlt}
          className={`${sizeClass} rounded-full object-cover`}
          draggable={false}
          loading="lazy"
          decoding="async"
        />
      )}
    </span>
  );
}
